import React, { useEffect, useState, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { SkeletonLine } from "@ui-components/Skeleton/GenericSkeleton";
import { ReactComponent as IconQuestion } from "@assets/Icons/Icon_Question.svg";
import {
  fetchOneCluster,
  fetchDevices,
  fetchInterfaces,
  updateCluster,
} from "../request/request";
import { useAppSelector } from "@redux/hooks";

function GenericSwitch({
  checked,
  onChange,
  disabled = false,
}: {
  checked: boolean;
  onChange: (val: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="relative inline-block w-11 h-6">
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        onChange={() => onChange(!checked)}
        disabled={disabled}
      />
      <div
        className={`
          absolute cursor-pointer top-0 left-0 right-0 bottom-0
          rounded-full transition-colors
          ${disabled ? "bg-gray-200" : "bg-gray-300 peer-focus:ring-2 peer-focus:ring-blue-300"}
          peer-checked:bg-blue-500
        `}
      />
      <div
        className={`
          absolute top-[2px] left-[2px] w-5 h-5 bg-white rounded-full
          transition-all duration-200
          ${checked ? "translate-x-5" : ""}
          ${disabled ? "cursor-not-allowed" : "cursor-pointer"}
        `}
      />
    </label>
  );
}

interface EditClusterProps {
  clusterId: number | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface DeviceItem {
  id: string;
  name: string;
  description: string;
}

interface IpItem {
  name: string;
  value: string;
}

interface ClusterData {
  id: number;
  name: string;
  description: string;
  enable: boolean;
  suscriptionId: number;
  ctxr: Array<{
    subid: string;
    name: string;
    description: string;
    localpref: number;
    ha_ip?: string;
  }>;
}

interface InitialValues {
  clusterName: string;
  clusterDescription: string;
  clusterEnable: boolean;
  subscriptionId: number | null;
  deviceR1Id: string;
  deviceR1Name: string;
  priorityR1: string;
  selectedIpR1: string;
  deviceR2Id: string;
  deviceR2Name: string;
  priorityR2: string;
  selectedIpR2: string;
}

export function EditCluster({
  clusterId,
  isOpen,
  onClose,
  onSuccess,
}: EditClusterProps) {
  const { getAccessTokenSilently } = useAuth0();
  const { tenantId } = useAppSelector(state => state.selection);
  const [isFetching, setIsFetching] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);

  const [clusterName, setClusterName] = useState("");
  const [clusterDescription, setClusterDescription] = useState("");
  const [clusterEnable, setClusterEnable] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState<number | null>(null);

  const [deviceR1Id, setDeviceR1Id] = useState("");
  const [deviceR1Name, setDeviceR1Name] = useState("");
  const [priorityR1, setPriorityR1] = useState("");
  const [selectedIpR1, setSelectedIpR1] = useState("");
  const [ipListR1, setIpListR1] = useState<IpItem[]>([]);

  const [deviceR2Id, setDeviceR2Id] = useState("");
  const [deviceR2Name, setDeviceR2Name] = useState("");
  const [priorityR2, setPriorityR2] = useState("");
  const [selectedIpR2, setSelectedIpR2] = useState("");
  const [ipListR2, setIpListR2] = useState<IpItem[]>([]);

  const [deviceList, setDeviceList] = useState<DeviceItem[]>([]);
  const [initialDevices, setInitialDevices] = useState<DeviceItem[]>([]);

  const [showPriorityR1Info, setShowPriorityR1Info] = useState(false);
  const [showPriorityR2Info, setShowPriorityR2Info] = useState(false);

  // Success and error alerts
  const [successAlertOpen, setSuccessAlertOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertDesc, setAlertDesc] = useState("");
  const [errorAlertOpen, setErrorAlertOpen] = useState(false);
  const [errorTitle, setErrorTitle] = useState("");
  const [errorDesc, setErrorDesc] = useState("");

  const [originalValues, setOriginalValues] = useState<InitialValues>({
    clusterName: "",
    clusterDescription: "",
    clusterEnable: false,
    subscriptionId: null,
    deviceR1Id: "",
    deviceR1Name: "",
    priorityR1: "",
    selectedIpR1: "",
    deviceR2Id: "",
    deviceR2Name: "",
    priorityR2: "",
    selectedIpR2: "",
  });

  useEffect(() => {
    if (!isOpen || !clusterId) return;
    loadClusterData(clusterId);
  }, [isOpen, clusterId]);

  async function loadClusterData(id: number) {
    try {
      setIsInitialLoading(true);
      const token = await getAccessTokenSilently();
      const resp = await fetchOneCluster(token, id, tenantId);
      const data: ClusterData = resp.data;

      setClusterName(data.name);
      setClusterDescription(data.description);
      setClusterEnable(data.enable);
      setSubscriptionId(data.suscriptionId);

      if (data.ctxr[0]) {
        setDeviceR1Id(data.ctxr[0].subid);
        setDeviceR1Name(data.ctxr[0].name);
        setPriorityR1(String(data.ctxr[0].localpref));
        setSelectedIpR1(data.ctxr[0].ha_ip || "");
      }
      if (data.ctxr[1]) {
        setDeviceR2Id(data.ctxr[1].subid);
        setDeviceR2Name(data.ctxr[1].name);
        setPriorityR2(String(data.ctxr[1].localpref));
        setSelectedIpR2(data.ctxr[1].ha_ip || "");
      }

      const temp: DeviceItem[] = [];
      if (data.ctxr[0]) {
        temp.push({
          id: data.ctxr[0].subid,
          name: data.ctxr[0].name,
          description: data.ctxr[0].description ?? "",
        });
      }
      if (data.ctxr[1]) {
        temp.push({
          id: data.ctxr[1].subid,
          name: data.ctxr[1].name,
          description: data.ctxr[1].description ?? "",
        });
      }
      setInitialDevices(temp);

      // Load all data in parallel
      const promises: Promise<void>[] = [];
      if (data.suscriptionId) {
        promises.push(loadDevices(data.suscriptionId));
      }
      if (data.ctxr[0]?.subid) {
        promises.push(loadIPs(data.ctxr[0].subid, "R1"));
      }
      if (data.ctxr[1]?.subid) {
        promises.push(loadIPs(data.ctxr[1].subid, "R2"));
      }

      await Promise.all(promises);

      setOriginalValues({
        clusterName: data.name,
        clusterDescription: data.description,
        clusterEnable: data.enable,
        subscriptionId: data.suscriptionId,
        deviceR1Id: data.ctxr[0]?.subid || "",
        deviceR1Name: data.ctxr[0]?.name || "",
        priorityR1: data.ctxr[0]?.localpref?.toString() || "",
        selectedIpR1: data.ctxr[0]?.ha_ip || "",
        deviceR2Id: data.ctxr[1]?.subid || "",
        deviceR2Name: data.ctxr[1]?.name || "",
        priorityR2: data.ctxr[1]?.localpref?.toString() || "",
        selectedIpR2: data.ctxr[1]?.ha_ip || "",
      });
    } catch (error) {
      console.error("Error loading cluster =>", error);
    } finally {
      setIsInitialLoading(false);
    }
  }

  async function loadDevices(subId: number) {
    try {
      const token = await getAccessTokenSilently();
      const resp = await fetchDevices(token, subId, tenantId);
      const unifiedList = unifyDevices(resp.data);
      setDeviceList(unifiedList);
    } catch (error) {
      console.error("Error fetching devices =>", error);
    }
  }

  function unifyDevices(backArr: DeviceItem[]): DeviceItem[] {
    const result = [...backArr];
    for (const initDev of initialDevices) {
      if (!result.some((d) => d.id === initDev.id)) {
        result.push(initDev);
      }
    }
    return result;
  }

  async function loadIPs(deviceId: string, which: "R1" | "R2") {
    try {
      const token = await getAccessTokenSilently();
      const resp = await fetchInterfaces(token, deviceId, tenantId);
      if (which === "R1") {
        setIpListR1(resp.data);
      } else {
        setIpListR2(resp.data);
      }
    } catch (error) {
      console.error("Error fetching IP list =>", error);
    }
  }

  async function handleSelectDeviceR1(newName: string) {
    setDeviceR1Name(newName);
    setSelectedIpR1("");
    setPriorityR1("");
    setIpListR1([]);
    const dev = deviceList.find((d) => d.name === newName);
    if (dev) {
      setDeviceR1Id(dev.id);
      await loadIPs(dev.id, "R1");
    } else {
      setDeviceR1Id("");
    }
  }

  async function handleSelectDeviceR2(newName: string) {
    setDeviceR2Name(newName);
    setSelectedIpR2("");
    setPriorityR2("");
    setIpListR2([]);
    const dev = deviceList.find((d) => d.name === newName);
    if (dev) {
      setDeviceR2Id(dev.id);
      await loadIPs(dev.id, "R2");
    } else {
      setDeviceR2Id("");
    }
  }

  function getFilteredDeviceListForR1(): DeviceItem[] {
    return deviceList.filter((d) => d.id !== deviceR2Id);
  }

  function getFilteredDeviceListForR2(): DeviceItem[] {
    return deviceList.filter((d) => d.id !== deviceR1Id);
  }

  function isFormValid() {
    return (
      clusterName.trim() &&
      deviceR1Id &&
      selectedIpR1 &&
      priorityR1 &&
      deviceR2Id &&
      selectedIpR2 &&
      priorityR2
    );
  }

  function getCurrentValues(): InitialValues {
    return {
      clusterName,
      clusterDescription,
      clusterEnable,
      subscriptionId,
      deviceR1Id,
      deviceR1Name,
      priorityR1,
      selectedIpR1,
      deviceR2Id,
      deviceR2Name,
      priorityR2,
      selectedIpR2,
    };
  }

  const isChanged = useMemo(() => {
    return (
      JSON.stringify(originalValues) !== JSON.stringify(getCurrentValues())
    );
  }, [originalValues, clusterName, clusterDescription, clusterEnable, subscriptionId, deviceR1Id, deviceR1Name, priorityR1, selectedIpR1, deviceR2Id, deviceR2Name, priorityR2, selectedIpR2]);

  async function handleSave() {
    if (!clusterId || !subscriptionId) {
      console.error("Missing clusterId or subscriptionId");
      return;
    }
    if (!deviceR1Id || !deviceR2Id) {
      alert("Please select both CTX-R1 and CTX-R2.");
      return;
    }
    if (deviceR1Id === deviceR2Id) {
      alert("No puedes seleccionar el mismo dispositivo para ambos CTX-R.");
      return;
    }
    const p1 = Number(priorityR1);
    const p2 = Number(priorityR2);
    if (p1 === p2) {
      alert("Las prioridades no pueden ser iguales. Verifica.");
      return;
    }
    if (p1 < 1 || p1 > 1000 || p2 < 1 || p2 > 1000) {
      alert("Priority must be between 1 and 1000.");
      return;
    }
    const payload = {
      name: clusterName,
      description: clusterDescription,
      enable: clusterEnable,
      suscriptionId: subscriptionId,
      ctxr: [
        { uuid: deviceR1Id, localpref: p1, ip: selectedIpR1 },
        { uuid: deviceR2Id, localpref: p2, ip: selectedIpR2 },
      ],
    };
    try {
      setIsFetching(true);
      const token = await getAccessTokenSilently();
      await updateCluster(token, clusterId, tenantId, payload);
      
      // Success
      setSuccessAlertOpen(true);
      setAlertTitle("Saved!");
      setAlertDesc("Your cluster has been updated successfully.");
    } catch (error) {
      console.error("Error updating cluster =>", error);
      // Error
      setErrorAlertOpen(true);
      setErrorTitle("Error on save");
      setErrorDesc("Could not update the cluster. Please try again.");
    } finally {
      setIsFetching(false);
    }
  }

  if (!isOpen) return null;

  return (
    <>
      <GenericModal
        isOpen={true}
        onClose={onClose}
        title="Edit Cluster"
        size="max-w-4xl"
      >
        {isInitialLoading ? (
          <div className="space-y-4">
            {/* Cluster On switch skeleton */}
            <div className="mb-4 flex items-center justify-end">
              <SkeletonLine w={100} h={20} />
            </div>
            {/* Form fields skeleton */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
              {/* Name */}
              <div className="space-y-2">
                <SkeletonLine w="40%" h={16} />
                <SkeletonLine w="100%" h={40} roundedClass="rounded-sm" />
              </div>
              {/* Description */}
              <div className="space-y-2">
                <SkeletonLine w="50%" h={16} />
                <SkeletonLine w="100%" h={40} roundedClass="rounded-sm" />
              </div>
              <div />
              {/* CTX-R 1 */}
              <div className="space-y-2">
                <SkeletonLine w="60%" h={16} />
                <SkeletonLine w="100%" h={40} roundedClass="rounded-sm" />
              </div>
              {/* IP for HA R1 */}
              <div className="space-y-2">
                <SkeletonLine w="50%" h={16} />
                <SkeletonLine w="100%" h={40} roundedClass="rounded-sm" />
              </div>
              {/* Priority R1 */}
              <div className="space-y-2">
                <SkeletonLine w="40%" h={16} />
                <SkeletonLine w="100%" h={40} roundedClass="rounded-sm" />
              </div>
              {/* CTX-R 2 */}
              <div className="space-y-2">
                <SkeletonLine w="60%" h={16} />
                <SkeletonLine w="100%" h={40} roundedClass="rounded-sm" />
              </div>
              {/* IP for HA R2 */}
              <div className="space-y-2">
                <SkeletonLine w="50%" h={16} />
                <SkeletonLine w="100%" h={40} roundedClass="rounded-sm" />
              </div>
              {/* Priority R2 */}
              <div className="space-y-2">
                <SkeletonLine w="40%" h={16} />
                <SkeletonLine w="100%" h={40} roundedClass="rounded-sm" />
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-4 flex items-center justify-end">
              <span className="mr-2 text-sm font-medium">Cluster On</span>
              <GenericSwitch
                checked={clusterEnable}
                onChange={(val) => setClusterEnable(val)}
                disabled={isFetching}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
              <GenericInput
                label="Name"
                value={clusterName}
                onChange={setClusterName}
              />
              <GenericInput
                label="Description"
                value={clusterDescription}
                onChange={setClusterDescription}
              />
              <div />
              <div>
                <label className="text-sm font-medium">
                  CTX-R 1 <span className="text-red-500">*</span>
                </label>
                <GenericDropdown
                  items={getFilteredDeviceListForR1().map((d) => d.name)}
                  value={deviceR1Name}
                  onSelect={handleSelectDeviceR1}
                  defaultLabel="Select a Device"
                  widthClass="w-full"
                  maxWidthClass="max-w-[400px]"
                />
              </div>
              <div>
                <label className="text-sm font-medium">
                  IP for HA <span className="text-red-500">*</span>
                </label>
                <GenericDropdown
                  items={ipListR1.map((ip) => ip.value)}
                  value={selectedIpR1}
                  onSelect={setSelectedIpR1}
                  defaultLabel="Select an IP"
                  widthClass="w-full"
                  maxWidthClass="max-w-[400px]"
                />
              </div>
              <div>
                <label className="text-sm font-medium flex items-center gap-1">
                  Priority <span className="text-red-500">*</span>
                  <IconQuestion
                    className="w-4 h-4 text-gray-500 cursor-pointer hover:text-gray-700"
                    onClick={() => setShowPriorityR1Info(true)}
                  />
                </label>
                <GenericInput value={priorityR1} onChange={setPriorityR1} />
              </div>
              <div>
                <label className="text-sm font-medium">
                  CTX-R 2 <span className="text-red-500">*</span>
                </label>
                <GenericDropdown
                  items={getFilteredDeviceListForR2().map((d) => d.name)}
                  value={deviceR2Name}
                  onSelect={handleSelectDeviceR2}
                  defaultLabel="Select a Device"
                  widthClass="w-full"
                  maxWidthClass="max-w-[400px]"
                />
              </div>
              <div>
                <label className="text-sm font-medium">
                  IP for HA <span className="text-red-500">*</span>
                </label>
                <GenericDropdown
                  items={ipListR2.map((ip) => ip.value)}
                  value={selectedIpR2}
                  onSelect={setSelectedIpR2}
                  defaultLabel="Select an IP"
                  widthClass="w-full"
                  maxWidthClass="max-w-[400px]"
                />
              </div>
              <div>
                <label className="text-sm font-medium flex items-center gap-1">
                  Priority <span className="text-red-500">*</span>
                  <IconQuestion
                    className="w-4 h-4 text-gray-500 cursor-pointer hover:text-gray-700"
                    onClick={() => setShowPriorityR2Info(true)}
                  />
                </label>
                <GenericInput value={priorityR2} onChange={setPriorityR2} />
              </div>
            </div>
          </>
        )}
        <div className="flex justify-end gap-2 mt-5">
          <GenericButton 
            variant="secondary" 
            label="Cancel" 
            onClick={onClose}
            disabled={isInitialLoading}
          />
          <GenericButton
            variant="brand"
            label={isFetching ? "Saving..." : "Save"}
            onClick={handleSave}
            disabled={!isFormValid() || !isChanged || isFetching || isInitialLoading}
            loading={isFetching}
          />
        </div>
      </GenericModal>
      {showPriorityR1Info && (
        <GenericAlert
          isOpen={true}
          onClose={() => setShowPriorityR1Info(false)}
          onConfirm={() => setShowPriorityR1Info(false)}
          title="Priority Explanation"
          description="This field determines the order in which announcements are made. A higher value indicates a higher priority for initiating announcements. When comparing two entries, the one with the greater value serves as the primary channel, and the one with the lesser value acts as a fallback."
          variant="warning"
          confirmLabel="Got it!"
        />
      )}
      {showPriorityR2Info && (
        <GenericAlert
          isOpen={true}
          onClose={() => setShowPriorityR2Info(false)}
          onConfirm={() => setShowPriorityR2Info(false)}
          title="Priority Explanation"
          description="This field determines the order in which announcements are made. A higher value indicates a higher priority for initiating announcements. When comparing two entries, the one with the greater value serves as the primary channel, and the one with the lesser value acts as a fallback."
          variant="warning"
          confirmLabel="Got it!"
        />
      )}
      {/* Success Alert */}
      <GenericAlert
        isOpen={successAlertOpen}
        onClose={() => {
          setSuccessAlertOpen(false);
          onClose();
          onSuccess();
        }}
        onConfirm={() => {
          setSuccessAlertOpen(false);
          onClose();
          onSuccess();
        }}
        title={alertTitle}
        description={alertDesc}
        cancelLabel="Close"
        confirmLabel="OK"
        variant="success"
      />

      {/* Error Alert */}
      <GenericAlert
        isOpen={errorAlertOpen}
        onClose={() => setErrorAlertOpen(false)}
        onConfirm={() => setErrorAlertOpen(false)}
        title={errorTitle}
        description={errorDesc}
        cancelLabel="Close"
        confirmLabel="OK"
        variant="error"
      />
    </>
  );
}
